import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PageHeader,
  Card,
  DataTable,
  Badge,
  Button,
  Toast,
  EmptyState,
  ConfirmModal,
} from '../ui';
import Drawer from './Drawer';
import {
  listCategoryTrees,
  createCategoryTree,
  updateCategoryTree,
  deleteCategoryTree,
  setCategoryTreeChannels,
  listChannels,
} from '../../services/pimApi';
import './pim.css';

const EMPTY_FORM = { code: '', label: '', description: '', channel_ids: [] };

function ChannelPicker({ channels, value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
      {channels.map((ch) => {
        const checked = value.includes(ch.id);
        return (
          <label key={ch.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={checked}
              onChange={() =>
                onChange(checked ? value.filter((id) => id !== ch.id) : [...value, ch.id])
              }
            />
            {ch.label || ch.code} ({ch.market_code})
          </label>
        );
      })}
    </div>
  );
}

export default function CategoryTrees() {
  const navigate = useNavigate();
  const [trees, setTrees] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [drawer, setDrawer] = useState(null); // {mode: 'create'|'edit', tree?}
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listCategoryTrees();
      setTrees(data.trees || []);
    } catch (e) {
      setToast({ message: 'Failed to load category trees', error: true });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    listChannels().then(setChannels).catch(() => {});
  }, [load]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setDrawer({ mode: 'create' });
  };

  const openEdit = (tree) => {
    setForm({
      code: tree.code,
      label: tree.label,
      description: tree.description || '',
      channel_ids: (tree.channels || []).map((c) => c.id),
    });
    setDrawer({ mode: 'edit', tree });
  };

  const save = async () => {
    if (busy) return;
    if (!form.label.trim() || (drawer.mode === 'create' && !form.code.trim())) {
      setToast({ message: 'Code and label are required', error: true });
      return;
    }
    setBusy(true);
    try {
      if (drawer.mode === 'create') {
        await createCategoryTree({
          code: form.code.trim(),
          label: form.label.trim(),
          description: form.description.trim() || null,
          channel_ids: form.channel_ids,
        });
        setToast({ message: `Tree "${form.label}" created` });
      } else {
        await updateCategoryTree(drawer.tree.code, {
          label: form.label.trim(),
          description: form.description.trim() || null,
        });
        await setCategoryTreeChannels(drawer.tree.code, form.channel_ids);
        setToast({ message: `Tree "${form.label}" updated` });
      }
      setDrawer(null);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setToast({ message: typeof detail === 'string' ? detail : 'Save failed', error: true });
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting || busy) return;
    setBusy(true);
    try {
      await deleteCategoryTree(deleting.code);
      setToast({ message: `Tree "${deleting.label}" deleted` });
      setDeleting(null);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setToast({ message: typeof detail === 'string' ? detail : 'Delete failed', error: true });
      setDeleting(null);
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    {
      key: 'label',
      header: 'Tree',
      render: (t) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <strong>{t.label}</strong>
          <code style={{ fontSize: 12, color: '#667' }}>{t.code}</code>
          {t.is_default && <Badge variant="info">Default</Badge>}
          {!t.enabled && <Badge variant="neutral">Disabled</Badge>}
        </span>
      ),
    },
    {
      key: 'channels',
      header: 'Channels',
      render: (t) =>
        (t.channels || []).length ? (
          <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
            {t.channels.map((c) => (
              <Badge key={c.id} variant="neutral">
                {c.label || c.code}
              </Badge>
            ))}
          </span>
        ) : (
          <span style={{ color: '#99a' }}>Not linked</span>
        ),
    },
    { key: 'category_count', header: 'Categories', render: (t) => t.category_count },
    { key: 'product_count', header: 'Products', render: (t) => t.product_count },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (t) => (
        <span style={{ display: 'inline-flex', gap: 6 }}>
          <Button size="sm" onClick={() => navigate(`/pim/categories/${encodeURIComponent(t.code)}`)}>
            Open
          </Button>
          <Button size="sm" variant="secondary" onClick={() => openEdit(t)}>
            Edit
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setDeleting(t)}>
            Delete
          </Button>
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Category trees"
        sub="Organise products into one or more taxonomies. Each tree can be linked to the channels it serves; the default web tree feeds Akeneo and BigCommerce category assignment."
        actions={<Button onClick={openCreate}>New tree</Button>}
      />

      <Card flush>
        <DataTable
          columns={columns}
          rows={trees}
          rowKey={(t) => t.code}
          loading={loading}
          empty={
            <EmptyState
              title="No category trees"
              body="Create a tree to start categorising products."
              action={<Button onClick={openCreate}>New tree</Button>}
            />
          }
        />
      </Card>

      {drawer && (
        <Drawer
          title={drawer.mode === 'create' ? 'New category tree' : `Edit tree: ${drawer.tree.label}`}
          sub="Trees group categories into a taxonomy; link a tree to the channels that use it."
          onClose={() => setDrawer(null)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setDrawer(null)}>Cancel</Button>
              <Button onClick={save} disabled={busy}>
                {drawer.mode === 'create' ? 'Create' : 'Save'}
              </Button>
            </>
          }
        >
          {drawer.mode === 'create' && (
            <div className="pim-field">
              <label>Code</label>
              <input
                type="text"
                value={form.code}
                placeholder="e.g. print, b2b_portal"
                onChange={(e) =>
                  setForm((f) => ({ ...f, code: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '_') }))
                }
              />
            </div>
          )}
          <div className="pim-field">
            <label>Label</label>
            <input
              type="text"
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
            />
          </div>
          <div className="pim-field">
            <label>Description</label>
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </div>
          <div className="pim-field">
            <label>Linked channels</label>
            <ChannelPicker
              channels={channels}
              value={form.channel_ids}
              onChange={(channel_ids) => setForm((f) => ({ ...f, channel_ids }))}
            />
          </div>
        </Drawer>
      )}

      {deleting && (
        <ConfirmModal
          title={`Delete tree "${deleting.label}"?`}
          body="Deletion is blocked while the tree still has product assignments. Categories in the tree are removed with it."
          confirmLabel="Delete"
          onConfirm={confirmDelete}
          onCancel={() => setDeleting(null)}
        />
      )}

      <Toast message={toast?.message} error={toast?.error} />
    </>
  );
}
